import { useState, type FormEvent } from "react";
import { Modal } from "./Modal";
import { QuotaContext, RESOURCE_LABEL } from "./QuotaContext";
import { Alert, Button, Field, Input, Textarea } from "./ui";
import { useDecideQuotaRequest } from "../lib/queries";
import type { QuotaRequest } from "../lib/types";

/**
 * The decision on one request to raise a ceiling, shared by the queue page
 * and the organization's own card so the two cannot drift.
 *
 * An approval may grant less than was asked -- the number is prefilled with
 * the request and editable -- and either decision may carry a note, which
 * is mailed to the organization's admins in the operator's own words. The
 * note is required on a denial: "no" with no reason is a ticket the next
 * day.
 */
export function QuotaDecisionModal({
  request,
  decision,
  onClose,
  onDecided,
}: {
  request: QuotaRequest | null;
  decision: "approved" | "denied";
  onClose: () => void;
  /** After the decision saved: the request, and what it became. */
  onDecided?: (request: QuotaRequest, decision: "approved" | "denied", granted: number | null) => void;
}) {
  const decide = useDecideQuotaRequest();
  const [granted, setGranted] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const open = request !== null;
  const grantedValue = granted ?? (request ? String(request.requestedLimit) : "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!request) return;
    setError(null);
    try {
      await decide.mutateAsync({
        id: request.id,
        decision,
        ...(decision === "approved" ? { grantedLimit: Number(grantedValue) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setGranted(null);
      setNote("");
      onDecided?.(request, decision, decision === "approved" ? Number(grantedValue) : null);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The decision could not be saved.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissable={!decide.isPending}
      title={decision === "approved" ? "Approve request" : "Deny request"}
      description={
        request
          ? `${request.organization.name} asked for ${request.requestedLimit} ${RESOURCE_LABEL[request.resource]} (now ${request.currentLimit}).`
          : undefined
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {request && (
          <div className="space-y-2">
            <QuotaContext request={request} />
            <p className="text-sm text-fg-muted">“{request.reason}”</p>
          </div>
        )}
        {decision === "approved" && (
          <Field label="New limit" hint="You can grant less than requested; explain in the note." htmlFor="quota-granted">
            <Input
              id="quota-granted"
              type="number"
              min={(request?.currentLimit ?? 0) + 1}
              step={1}
              value={grantedValue}
              onChange={(e) => setGranted(e.target.value)}
              required
            />
          </Field>
        )}
        <Field
          label={decision === "approved" ? "Note (optional)" : "Note"}
          hint="Sent to the organization's admins as written."
          htmlFor="quota-note"
        >
          <Textarea id="quota-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} required={decision === "denied"} maxLength={2000} />
        </Field>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant={decision === "approved" ? "primary" : "danger"} loading={decide.isPending}>
            {decision === "approved" ? "Approve" : "Deny"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
